import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAuthStore } from '@stores/AuthStore.js'

/**
 * The subscriber's async chat with their tax agent (SUB-MVP-42). Lists threads,
 * opens one (marking it read), sends replies, and starts new conversations. Not
 * persisted — re-fetched on every visit. Mirrors ProfileStore's Bearer auth +
 * error handling.
 */
export const useChatStore = defineStore('chatStore', () => {
  const authStore = useAuthStore()

  const threads = ref([])
  const activeId = ref(null)
  const messages = ref([])
  const loading = ref(false)
  const loaded = ref(false) // a first successful thread load has happened
  const loadError = ref(false)
  const opening = ref(false)
  const openingError = ref(false)
  const sending = ref(false)
  const sendError = ref(false)
  const starting = ref(false)
  const startError = ref(false)

  const headers = () => ({ Authorization: `Bearer ${authStore.token}` })

  const handleAuthError = (error) => {
    if ([401, 403].includes(error?.response?.status)) {
      authStore.clearSession()
      if (!import.meta.env.SSR) window.location.href = '/'
      return true
    }
    return false
  }

  // Follow pagination to the end so older threads aren't dropped.
  const fetchAllThreads = async () => {
    const all = []
    for (let page = 1; page <= 50; page++) {
      const { data } = await axios.get('/api/client/threads', { params: { page }, headers: headers() })
      all.push(...(data.data ?? []))
      const meta = data.meta ?? {}
      if (!meta.last_page || page >= meta.last_page) break
    }
    return all
  }

  // Stale-while-revalidate: only show the blocking spinner on the first load.
  // A revisit keeps the cached thread list on screen and refreshes silently.
  const loadThreads = async () => {
    const spin = !loaded.value
    if (spin) loading.value = true
    loadError.value = false
    try {
      threads.value = await fetchAllThreads()
      loaded.value = true
    } catch (error) {
      if (!handleAuthError(error)) loadError.value = true
    } finally {
      if (spin) loading.value = false
    }
  }

  const openThread = async (id) => {
    activeId.value = id
    messages.value = []
    opening.value = true
    openingError.value = false
    sendError.value = false
    try {
      const { data } = await axios.get(`/api/client/threads/${id}`, { headers: headers() })
      // The user may have switched threads while this was in flight — don't let
      // a late response paint thread A's messages under thread B's header.
      if (activeId.value !== id) return
      messages.value = data.data?.messages ?? []
      // Mark read (best-effort) and zero the unread badge in the list.
      markRead(id)
    } catch (error) {
      if (activeId.value === id && !handleAuthError(error)) openingError.value = true
    } finally {
      if (activeId.value === id) opening.value = false
    }
  }

  const markRead = async (id) => {
    try {
      await axios.post(`/api/client/threads/${id}/read`, {}, { headers: headers() })
      const thread = threads.value.find((t) => t.id === id)
      if (thread) thread.unread_count = 0
    } catch {
      // Non-critical: the unread badge staying is harmless.
    }
  }

  const closeThread = () => {
    activeId.value = null
    messages.value = []
    opening.value = false
  }

  const send = async (body) => {
    const text = body.trim()
    const targetId = activeId.value
    if (!text || targetId == null) return false
    sending.value = true
    sendError.value = false
    try {
      const { data } = await axios.post(
        `/api/client/threads/${targetId}/messages`,
        { body: text },
        { headers: headers() }
      )
      const message = data.data ?? data
      // Only paint the reply if the user is still in the thread they sent to
      // (the server stored it correctly regardless).
      if (activeId.value === targetId) messages.value = [...messages.value, message]
      touchThread(targetId, message)
      return true
    } catch (error) {
      if (!handleAuthError(error)) sendError.value = true
      return false
    } finally {
      sending.value = false
    }
  }

  const startThread = async ({ subject, body }) => {
    const text = (body ?? '').trim()
    if (!text) return false
    starting.value = true
    startError.value = false
    try {
      const { data } = await axios.post(
        '/api/client/threads',
        { subject: subject?.trim() || null, body: text },
        { headers: headers() }
      )
      const thread = data.data ?? data
      threads.value = [thread, ...threads.value]
      // Open the new thread so the client sees their message land.
      activeId.value = thread.id
      // The create response only carries latest_message, which is the client's own first message.
      const seeded = thread.messages?.length ? thread.messages : [thread.latest_message].filter(Boolean)
      messages.value = seeded
      if (!seeded.length) openThread(thread.id)
      return true
    } catch (error) {
      if (!handleAuthError(error)) startError.value = true
      return false
    } finally {
      starting.value = false
    }
  }

  // Move a thread to the top and refresh its preview after a new message.
  const touchThread = (id, message = null) => {
    const thread = threads.value.find((t) => t.id === id)
    if (!thread) return
    thread.last_message_at = message?.created_at ?? new Date().toISOString()
    if (message) thread.latest_message = message
    threads.value = [thread, ...threads.value.filter((t) => t.id !== id)]
  }

  const resetErrors = () => {
    openingError.value = false
    sendError.value = false
    startError.value = false
    loadError.value = false
  }

  const reset = () => {
    threads.value = []
    activeId.value = null
    messages.value = []
    loading.value = false
    loaded.value = false
    loadError.value = false
    opening.value = false
    openingError.value = false
    sending.value = false
    sendError.value = false
    starting.value = false
    startError.value = false
  }

  return {
    threads,
    activeId,
    messages,
    loading,
    loadError,
    opening,
    openingError,
    sending,
    sendError,
    starting,
    startError,
    loadThreads,
    openThread,
    closeThread,
    send,
    startThread,
    resetErrors,
    reset,
  }
})
